import api from '@/services/api/client';
import type {
  GuidedSchedule,
  JobListResponse,
  ManualRunResponse,
  QueueFilters,
  QueueItem,
  QueueSource,
  QueueListResponse,
  ScheduledJob,
  SchedulePreviewResponse,
  UpdateJobInput,
} from './types';

const serializeSchedule = (schedule: GuidedSchedule): GuidedSchedule => {
  const startDate = schedule.startDate?.trim();
  const start = startDate ? {startDate} : {};
  switch (schedule.kind) {
    case 'interval':
      return {kind: 'interval', intervalMinutes: schedule.intervalMinutes, ...start};
    case 'daily':
      return {kind: 'daily', time: schedule.time, ...start};
    case 'weekly':
      return {kind: 'weekly', weekday: schedule.weekday, time: schedule.time, ...start};
  }
};

const configWithSignal = (signal?: AbortSignal) => signal ? {signal} : undefined;

const queueParams = (filters: QueueFilters = {}): URLSearchParams => {
  const params = new URLSearchParams();
  filters.state?.forEach(state => params.append('state', state));
  filters.source?.forEach(source => params.append('source', source));
  if (filters.jobKey) params.set('jobKey', filters.jobKey);
  if (filters.workspaceId) params.set('workspaceId', filters.workspaceId);
  if (filters.search) params.set('search', filters.search);
  if (filters.cursor) params.set('cursor', filters.cursor);
  if (filters.limit !== undefined) params.set('limit', String(filters.limit));
  return params;
};

export const operationsAPI = {
  async listJobs(signal?: AbortSignal): Promise<JobListResponse> {
    const response = await api.get<JobListResponse>('/admin/operations/jobs', {signal});
    return response.data;
  },

  async previewSchedule(jobKey: string, schedule: GuidedSchedule, signal?: AbortSignal): Promise<SchedulePreviewResponse> {
    const response = await api.post<SchedulePreviewResponse>(
      `/admin/operations/jobs/${encodeURIComponent(jobKey)}/schedule-preview`,
      {schedule: serializeSchedule(schedule)},
      configWithSignal(signal),
    );
    return response.data;
  },

  async updateJob(jobKey: string, input: UpdateJobInput, signal?: AbortSignal): Promise<ScheduledJob> {
    const response = await api.put<ScheduledJob>(
      `/admin/operations/jobs/${encodeURIComponent(jobKey)}`,
      {...input, schedule: serializeSchedule(input.schedule)},
      configWithSignal(signal),
    );
    return response.data;
  },

  async runNow(jobKey: string, signal?: AbortSignal): Promise<ManualRunResponse> {
    const response = await api.post<ManualRunResponse>(
      `/admin/operations/jobs/${encodeURIComponent(jobKey)}/runs`,
      {},
      configWithSignal(signal),
    );
    return response.data;
  },

  async listQueue(filters: QueueFilters = {}, signal?: AbortSignal): Promise<QueueListResponse> {
    const response = await api.get<QueueListResponse>('/admin/operations/queue', {
      params: queueParams(filters),
      signal,
    });
    return response.data;
  },

  async cancelQueueItem(source: QueueSource, runId: string, signal?: AbortSignal): Promise<QueueItem> {
    const response = await api.post<{item: QueueItem}>(
      `/admin/operations/queue/${encodeURIComponent(source)}/${encodeURIComponent(runId)}/cancellation`,
      {},
      configWithSignal(signal),
    );
    return response.data.item;
  },

  async recoverQueueItem(source: QueueSource, runId: string, signal?: AbortSignal): Promise<QueueItem> {
    const response = await api.post<{item: QueueItem}>(
      `/admin/operations/queue/${encodeURIComponent(source)}/${encodeURIComponent(runId)}/recovery`,
      {},
      configWithSignal(signal),
    );
    return response.data.item;
  },
};
